import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import PropTypes from "prop-types"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { useSelector } from "react-redux"
import PageHeader from "../../../components/DashboardComponents/PageHeader"
import DashboardCard from "../../../components/DashboardComponents/DashboardCard"
import ListItem from "../../../components/DashboardComponents/ListItem"
import CustomButton from "../../../components/OnboardingComponents/CustomButton"
import EmptyState from "../../../components/Common/EmptyState"
import { ICONS } from "../../../constants/icons"
import { get } from "../../../api/axios"
import endpoints from "../../../api/endpoints"
import { toastApiError } from "../../../helpers/apiError"
import { formatDashboardGreeting } from "../../../helpers/timeGreeting"
import { adminCityNavState } from "../../../helpers/adminCity"
import useAdminPermission from "../../../hooks/useAdminPermission"
import MembersByCityDonut from "../Dashboard/MembersByCityDonut"
import {
  CITY_GROWTH_PERIOD_OPTIONS,
  DEFAULT_CITY_GROWTH_PERIOD,
  getCityPendingTotal,
  mapCityDetailKpi,
  mapCityMemberGrowth,
  mapCityPendingItems,
  mapCityRecentActivities,
  mapCitySpellbookSegments,
} from "./adminCityDetailData"
import { fetchCitySpellbookSummary } from "../../Profile/profileGenome"

const PENDING_ICONS = {
  star: ICONS.pendingStar,
  search: ICONS.pendingSearchStatus,
  camera: ICONS.pendingCamera,
  flag: ICONS.pendingFlag,
  transfer: ICONS.pendingArrangeCircle,
}

const ACTIVITY_ICONS = {
  user: ICONS.activityUserBlue,
  search: ICONS.activitySearchStatusBlue,
  award: ICONS.activityAwardBlue,
  inbox: ICONS.activityDirectInboxBlue,
  map: ICONS.activityMapBlue,
  flag: ICONS.activityFlag,
}

const formatLocation = city => {
  if (!city) return ""
  const name = city.name || city.city || ""
  if (city.state && name) return `${name}, ${city.state}`
  return name || ""
}

const mapInitialCityState = (source, fallbackId = "") => {
  if (!source && !fallbackId) {
    return { id: "", name: "City", state: "", country: "" }
  }
  return {
    id: source?.id || fallbackId,
    name: source?.name || source?.city || "City",
    state: source?.state || "",
    country: source?.country || "",
  }
}

const MemberGrowthBars = ({ heights = [], colors = [], labels = [] }) => (
  <div className="admin-city-detail__growth">
    <div className="admin-city-detail__growth-bars" aria-hidden="true">
      {heights.map((height, index) => (
        <span
          key={`bar-${index}`}
          className="admin-city-detail__growth-bar"
          style={{
            height: `${height}px`,
            backgroundColor: colors[index] || "#ff822f",
          }}
        />
      ))}
    </div>
    <div className="admin-city-detail__growth-labels">
      {labels.map((label, index) => (
        <span key={`${label}-${index}`}>{label}</span>
      ))}
    </div>
  </div>
)

/**
 * Admin City Detail Dashboard — GET /api/v1/admin/dashboard/stats?city_id=
 * Optional `cityId` / `initialCity` embed this as city-admin home (header city).
 */
const AdminCityDetail = ({ cityId: cityIdProp = "", initialCity = null }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { cityId: cityIdParam } = useParams()
  const cityId = String(cityIdProp || cityIdParam || "").trim()
  const currentUser = useSelector(state => state.auth.currentUser)
  const { can } = useAdminPermission()
  const canViewActivityLogs = can("activity-logs", "view")
  const canViewSpellbooks = can("spellbooks", "view")
  const firstName = currentUser?.first_name || "Jerry"

  const greetingTitle = useMemo(
    () => formatDashboardGreeting(firstName),
    [firstName],
  )

  const cityFromNav = initialCity || location.state?.city
  const [city, setCity] = useState(() =>
    mapInitialCityState(cityFromNav, cityId),
  )
  const [growthPeriod, setGrowthPeriod] = useState(DEFAULT_CITY_GROWTH_PERIOD)
  const [periodOpen, setPeriodOpen] = useState(false)
  const periodRef = useRef(null)

  const [kpiCards, setKpiCards] = useState(() => mapCityDetailKpi())
  const [pendingItems, setPendingItems] = useState(() => mapCityPendingItems())
  const [pendingTotal, setPendingTotal] = useState(0)
  const [memberGrowth, setMemberGrowth] = useState(() => mapCityMemberGrowth())
  const [recentActivities, setRecentActivities] = useState([])
  const [isActivityLoading, setIsActivityLoading] = useState(true)
  const [spellbook, setSpellbook] = useState([])
  const [isSpellbookLoading, setIsSpellbookLoading] = useState(true)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  const locationLabel = formatLocation(city)
  const growthPeriodLabel = useMemo(
    () =>
      CITY_GROWTH_PERIOD_OPTIONS.find(option => option.value === growthPeriod)
        ?.label || growthPeriod,
    [growthPeriod],
  )

  useEffect(() => {
    document.title = `${city.name} | AMOK . EARTH Admin`
  }, [city.name])

  useEffect(() => {
    if (!periodOpen) return undefined
    const onPointerDown = event => {
      if (periodRef.current && !periodRef.current.contains(event.target)) {
        setPeriodOpen(false)
      }
    }
    document.addEventListener("mousedown", onPointerDown)
    return () => document.removeEventListener("mousedown", onPointerDown)
  }, [periodOpen])

  useEffect(() => {
    if (cityFromNav?.id && String(cityFromNav.id) === String(cityId)) {
      setCity(mapInitialCityState(cityFromNav, cityId))
      return undefined
    }

    // No GET /cities/:id on the API — resolve name from the cities list.
    let cancelled = false
    const resolveCityFromList = async () => {
      if (!cityId) return
      try {
        const response = await get(endpoints.getAdminCities, {
          params: { page: 1, page_size: 100 },
        })
        if (cancelled || !response?.data?.success) return
        const rows = response.data.result?.data || []
        const match = rows.find(item => String(item.id) === String(cityId))
        if (!match) return
        setCity(mapInitialCityState(match, cityId))
      } catch {
        // Keep fallback city name
      }
    }
    resolveCityFromList()
    return () => {
      cancelled = true
    }
  }, [cityFromNav, cityId])

  const fetchStats = useCallback(async () => {
    if (!cityId) return
    setIsLoading(true)
    setError(null)
    try {
      const response = await get(endpoints.getAdminDashboardStats, {
        params: {
          city_id: cityId,
          period: growthPeriod,
        },
      })

      if (response?.data?.success) {
        const result = response.data.result || {}
        setKpiCards(mapCityDetailKpi(result))
        setPendingItems(mapCityPendingItems(result))
        setPendingTotal(getCityPendingTotal(result))
        setMemberGrowth(mapCityMemberGrowth(result))
      } else {
        const message = toastApiError(response, "Failed to load city stats")
        setError(message)
      }
    } catch (err) {
      const message = toastApiError(err, "Failed to load city stats")
      setError(message)
    } finally {
      setIsLoading(false)
    }
  }, [cityId, growthPeriod])

  useEffect(() => {
    fetchStats()
  }, [fetchStats])

  useEffect(() => {
    let cancelled = false
    const fetchRecentActivities = async () => {
      const resolvedCityId = String(cityId || "").trim()
      if (!canViewActivityLogs || !resolvedCityId) {
        setRecentActivities([])
        setIsActivityLoading(false)
        return
      }

      setIsActivityLoading(true)
      try {
        // Same contract as Admin Activity Log — scopes by city_id query param.
        const response = await get(endpoints.getAdminActivityLogs, {
          params: {
            city_id: resolvedCityId,
            page: 1,
            page_size: 5,
            search: "",
            sort_by: "created_at",
            sort_direction: "desc",
          },
          skipErrorToast: true,
        })
        if (cancelled) return
        if (response?.data?.success) {
          setRecentActivities(
            mapCityRecentActivities(
              response.data.result || {},
              5,
              resolvedCityId,
            ),
          )
        } else {
          toastApiError(response, "Failed to load recent activities")
          setRecentActivities([])
        }
      } catch (err) {
        if (!cancelled) {
          toastApiError(err, "Failed to load recent activities")
          setRecentActivities([])
        }
      } finally {
        if (!cancelled) setIsActivityLoading(false)
      }
    }
    fetchRecentActivities()
    return () => {
      cancelled = true
    }
  }, [canViewActivityLogs, cityId])

  useEffect(() => {
    let cancelled = false
    const loadSpellbook = async () => {
      if (!cityId || !canViewSpellbooks) {
        setSpellbook([])
        setIsSpellbookLoading(false)
        return
      }
      setIsSpellbookLoading(true)
      try {
        const summary = await fetchCitySpellbookSummary(cityId)
        if (!cancelled) {
          setSpellbook(mapCitySpellbookSegments(summary))
        }
      } catch (err) {
        if (!cancelled) {
          toastApiError(err, "Failed to load arts genome")
          setSpellbook([])
        }
      } finally {
        if (!cancelled) setIsSpellbookLoading(false)
      }
    }
    loadSpellbook()
    return () => {
      cancelled = true
    }
  }, [canViewSpellbooks, cityId])

  const spellbookTotal = useMemo(
    () => spellbook.reduce((sum, item) => sum + (Number(item.percent) || 0), 0),
    [spellbook],
  )

  const handleDownloadPdf = () => {
    const previousTitle = document.title
    const safeName =
      String(city.name || "City")
        .replace(/[^\w\s-]/g, "")
        .trim() || "City"
    document.title = `${safeName} City Report`

    const restoreTitle = () => {
      document.title = previousTitle
      window.removeEventListener("afterprint", restoreTitle)
    }
    window.addEventListener("afterprint", restoreTitle)
    window.print()
  }

  if (!cityId) {
    return (
      <div className="admin-page admin-city-detail">
        <EmptyState
          title="No city selected"
          description="Choose a city from the header to view city stats."
        />
      </div>
    )
  }

  return (
    <div className="admin-page admin-city-detail">
      <PageHeader
        className="admin-city-detail__header"
        title={greetingTitle}
        subtitle={`Here's what's happening in ${locationLabel || city.name}`}
        actions={
          <div className="admin-city-detail__header-actions">
            <div className="admin-city-detail__period" ref={periodRef}>
              <CustomButton
                variant="transparent"
                size="md"
                type="button"
                trailingIconSrc={ICONS.chevronDown}
                trailingIconAlt=""
                aria-haspopup="listbox"
                aria-expanded={periodOpen}
                onClick={() => setPeriodOpen(open => !open)}
              >
                {growthPeriodLabel}
              </CustomButton>
              {periodOpen ? (
                <ul className="admin-city-detail__period-menu" role="listbox">
                  {CITY_GROWTH_PERIOD_OPTIONS.map(option => (
                    <li key={option.value}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={option.value === growthPeriod}
                        className={
                          option.value === growthPeriod
                            ? "admin-city-detail__period-option admin-city-detail__period-option--active"
                            : "admin-city-detail__period-option"
                        }
                        onClick={() => {
                          setGrowthPeriod(option.value)
                          setPeriodOpen(false)
                        }}
                      >
                        {option.label}
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
            <CustomButton
              variant="primary"
              size="md"
              type="button"
              className="admin-city-detail__export-btn admin-city-detail__download-pdf-btn"
              trailingIconSrc={ICONS.adminDocumentDownload}
              trailingIconAlt=""
              onClick={handleDownloadPdf}
            >
              Download PDF
            </CustomButton>
          </div>
        }
      />

      {error && !isLoading ? (
        <EmptyState
          title="Unable to load city stats"
          description={error}
          actionText="Try again"
          onActionClick={fetchStats}
        />
      ) : null}

      <div
        className="admin-city-detail__kpi-grid"
        aria-busy={isLoading || undefined}
      >
        {kpiCards.map(card => (
          <DashboardCard
            key={card.key}
            title={card.title}
            tone={card.tone}
            className={`admin-city-detail__kpi-card admin-city-detail__kpi-card--${card.tone}`}
            topRight={
              !card.module || can(card.module, "view") ? (
                <CustomButton
                  variant="transparent"
                  size="sm"
                  type="button"
                  className="admin-city-detail__link-btn"
                  trailingIconSrc={ICONS.arrowRight12}
                  trailingIconAlt=""
                  onClick={() => {
                    const id = city.id || cityId
                    if (!id) return
                    const scoped = {
                      members: `/admin/cities/${id}/members`,
                      projects: `/admin/cities/${id}/projects`,
                      "shadow-working-events": `/admin/cities/${id}/shadow-working-events`,
                    }
                    navigate(
                      scoped[card.to] ||
                        `/admin/${card.to}?city_id=${encodeURIComponent(id)}`,
                      adminCityNavState(city),
                    )
                  }}
                >
                  View All
                </CustomButton>
              ) : null
            }
          >
            <div className="admin-city-detail__kpi-footer">
              <span className="admin-city-detail__kpi-value">
                {isLoading ? "…" : card.value}
              </span>
              <span className="admin-city-detail__kpi-label">{card.label}</span>
            </div>
          </DashboardCard>
        ))}
      </div>

      <section className="admin-city-detail__pending">
          <div className="admin-city-detail__pending-head">
            <h2 className="admin-city-detail__pending-title">
              Pending Items Requiring Attention
            </h2>
            <span className="admin-city-detail__pending-tag">
              {isLoading ? "…" : `${pendingTotal} items`}
            </span>
          </div>
          <div className="admin-city-detail__pending-grid">
            {pendingItems.map(item => (
              <div key={item.key} className="admin-city-detail__pending-card">
                <div className="admin-city-detail__pending-top">
                  <div className="admin-city-detail__pending-copy">
                    <div className="admin-city-detail__pending-title-row">
                      <span className="admin-city-detail__pending-name">
                        {item.title}
                      </span>
                      <span className="admin-city-detail__pending-count">
                        {isLoading ? "…" : item.count}
                      </span>
                    </div>
                    <p className="admin-city-detail__pending-subtitle">
                      {item.subtitle}
                    </p>
                  </div>
                  <span className="admin-city-detail__pending-icon-wrap">
                    <img
                      src={PENDING_ICONS[item.icon]}
                      alt=""
                      width={20}
                      height={20}
                    />
                  </span>
                </div>
                {!item.module || can(item.module, "view") ? (
                  <CustomButton
                    variant="primary"
                    size="sm"
                    type="button"
                    trailingIconSrc={ICONS.arrowRight12}
                    trailingIconAlt=""
                    className="admin-city-detail__pending-action"
                    onClick={() => {
                      const id = city.id || cityId
                      if (!id) return
                      const scoped = {
                        reviews: `/admin/cities/${id}/reviews`,
                        calls: `/admin/cities/${id}/calls`,
                        photography: `/admin/cities/${id}/photography`,
                        flags: `/admin/cities/${id}/flags`,
                        transfers: `/admin/cities/${id}/transfers`,
                      }
                      navigate(
                        scoped[item.key] ||
                          `/admin/cities/${id}?city_id=${encodeURIComponent(id)}`,
                        adminCityNavState(city),
                      )
                    }}
                  >
                    View All
                  </CustomButton>
                ) : null}
              </div>
            ))}
          </div>
        </section>

      <div className="admin-city-detail__bottom">
        <DashboardCard
          title="Member Growth Overview"
          subtitle={`${locationLabel || city.name} - ${growthPeriodLabel}`}
          className="admin-city-detail__growth-card dcard--no-justify"
        >
            {memberGrowth.heights.length === 0 && !isLoading ? (
              <EmptyState
                title="No growth data"
                description="Member growth will show up here once available."
              />
            ) : (
              <>
                <MemberGrowthBars
                  heights={memberGrowth.heights}
                  colors={memberGrowth.colors}
                  labels={memberGrowth.labels}
                />
                <div className="admin-city-detail__growth-stats">
                  {memberGrowth.stats.map(stat => (
                    <div
                      key={stat.label}
                      className="admin-city-detail__growth-stat"
                    >
                      <span className="admin-city-detail__growth-stat-value">
                        {isLoading ? "…" : stat.value}
                      </span>
                      <span className="admin-city-detail__growth-stat-label">
                        {stat.label}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </DashboardCard>

        <DashboardCard
            title={`${city.name} at a Glance`}
            subtitle="Arts Genome - Spellbook"
            tone="lightteal"
            className="admin-city-detail__genome-card dcard--no-justify"
            topRight={
              isSpellbookLoading ? (
                <span
                  className="admin-city-detail__skeleton admin-city-detail__skeleton--donut"
                  aria-hidden="true"
                />
              ) : (
                <MembersByCityDonut
                  segments={spellbook}
                  size={120}
                  strokeWidth={22}
                />
              )
            }
          >
            {isSpellbookLoading ? (
              <ul className="admin-city-detail__genome-list" aria-busy="true">
                {Array.from({ length: 5 }, (_, index) => (
                  <li
                    key={`spellbook-skel-${index}`}
                    className="admin-city-detail__genome-row"
                  >
                    <span className="admin-city-detail__skeleton admin-city-detail__skeleton--genome" />
                  </li>
                ))}
              </ul>
            ) : spellbook.length > 0 ? (
              <ul className="admin-city-detail__genome-list">
                {spellbook.map(item => (
                  <li key={item.name} className="admin-city-detail__genome-row">
                    <span
                      className="admin-city-detail__genome-dot"
                      style={{ backgroundColor: item.color }}
                      aria-hidden="true"
                    />
                    <span className="admin-city-detail__genome-label">
                      {item.name}
                    </span>
                    <span className="admin-city-detail__genome-pct">
                      {item.percent}%
                    </span>
                  </li>
                ))}
                <li className="admin-city-detail__genome-row admin-city-detail__genome-row--total">
                  <span className="admin-city-detail__genome-label">Total</span>
                  <span className="admin-city-detail__genome-pct">
                    {Math.min(100, Math.round(spellbookTotal))}%
                  </span>
                </li>
              </ul>
            ) : (
              <EmptyState
                title="No spellbook data"
                description="Arts genome for this city will show up here."
              />
            )}
          </DashboardCard>

        {canViewActivityLogs ? (
          <DashboardCard
            title="Recent Activities"
            subtitle="Latest city events"
            tone="lightblue-custom"
            className="admin-city-detail__activity-card dcard--no-justify"
            topRight={
              <CustomButton
                variant="transparent"
                size="sm"
                type="button"
                className="admin-city-detail__link-btn admin-city-detail__link-btn--blue"
                trailingIconSrc={ICONS.arrowRight12}
                trailingIconAlt=""
                onClick={() =>
                  navigate(
                    `/admin/activity-log?city_id=${encodeURIComponent(cityId)}`,
                  )
                }
              >
                View All
              </CustomButton>
            }
          >
            <div className="admin-city-detail__activity-list">
              {isActivityLoading ? (
                Array.from({ length: 4 }).map((_, index) => (
                  <div
                    key={`activity-skel-${index}`}
                    className="admin-city-detail__activity-skeleton"
                    aria-hidden="true"
                  />
                ))
              ) : recentActivities.length === 0 ? (
                <EmptyState
                  title="No recent activity"
                  description="City activity will show up here once available."
                />
              ) : (
                recentActivities.map(item => (
                  <ListItem
                    key={item.id}
                    leadingIcon={
                      ACTIVITY_ICONS[item.icon] || ACTIVITY_ICONS.user
                    }
                    title={item.title}
                    subtitle={item.subtitle}
                    trailing={
                      <span className="admin-city-detail__activity-time">
                        {item.time}
                      </span>
                    }
                    bordered
                  />
                ))
              )}
            </div>
          </DashboardCard>
        ) : null}
      </div>
    </div>
  )
}

AdminCityDetail.propTypes = {
  cityId: PropTypes.string,
  initialCity: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    country: PropTypes.string,
  }),
}

AdminCityDetail.defaultProps = {
  cityId: "",
  initialCity: null,
}

export default AdminCityDetail
